import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal } from 'antd';
import {
  useModalMethods,
  useModalWatch,
} from '../../../../shared/packages/modalManager';
import { modalNames } from '../../../../shared/configs/modal';
import { useCurrentAcceptance } from '../../../../services/queries/acceptanceData';
import { useFormController } from '../../../../../../helpers/formManager';
import { dateTime } from '../../../../shared/packages/dateTime';
import {
  acceptanceDateMethodNames,
  useAcceptanceDateMutation,
} from '../../../../api/acceptanceDateMutation';
import { useAcceptanceViewInfoInvalidate } from '../../../../api/acceptanceViewInfo';
import { useEvent } from '../../../../../../hooks/useEvent';
import {
  createDefaultValues,
  createFooterProps,
  getBody,
  getMainFieldName,
  getModalTitle,
  getMutateVariablesCreator,
  getToNewConfigOptionsCreator,
} from './utils';
import Footer from './Footer';
import { useAcceptanceViewFlatsInvalidate } from '../../../../api/acceptanceViewFlats';
import { useAcceptanceViewCalendarInvalidate } from '../../../../api/acceptanceViewCalendar';
import styles from './index.module.scss';

const modalName = modalNames.changeAcceptanceDate;

const ChangeAcceptanceDateModal = () => {
  const history = useHistory();

  const [mode, setMode] = useState(0);
  const [serverMessage, setServerMessage] = useState('');
  const [checkPayload, setCheckPayload] = useState();

  const { isOpen, props: { mutableEntity } = {} } = useModalWatch({
    name: modalName,
  });

  const acceptance = useCurrentAcceptance();

  const createMutateVariables = getMutateVariablesCreator(mutableEntity);
  const createToNewConfigOptions = getToNewConfigOptionsCreator(mutableEntity, {
    fromDate: acceptance.fromDate,
    toDate: acceptance.toDate,
  });
  const fieldName = getMainFieldName(mutableEntity);

  const { closeAndKeepProps, removeProps } = useModalMethods();

  const defaultValues = useMemo(
    () => createDefaultValues(acceptance),
    [acceptance]
  );

  const { control, reset, handleSubmit, setError, getValues } =
    useFormController({
      mode: 'onChange',
      defaultValues: createDefaultValues(acceptance),
    });

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const invalidateAcceptanceViewInfo = useAcceptanceViewInfoInvalidate();
  const invalidateFlats = useAcceptanceViewFlatsInvalidate();
  const invalidateCalendarView = useAcceptanceViewCalendarInvalidate();

  const { mutate, isLoading } = useAcceptanceDateMutation();

  const closeModal = useEvent(() => {
    closeAndKeepProps(modalName);
  });

  const resetMode = useEvent(() => {
    setMode(0);
    setServerMessage('');
    setCheckPayload(undefined);
  });

  const invalidate = useEvent(() => {
    invalidateFlats();
    invalidateCalendarView();
    invalidateAcceptanceViewInfo();

    closeModal();
  });

  const toNewConfig = useEvent(() => {
    history.push(createToNewConfigOptions(history.location, getValues()));
  });

  const checkDate = useEvent((values) => {
    if (values.newConfig) {
      toNewConfig();
      return;
    }

    const variables = createMutateVariables({
      values,
      methodName: acceptanceDateMethodNames.check,
    });

    mutate(variables, {
      onSuccess: async (data) => {
        if (data === 'Error') return;

        if (data.state === 'Error') {
          setError(fieldName, {
            type: 'server',
            message: data.message || 'Не удалось изменить дату',
          });
          return;
        }

        if (data.code == null) {
          invalidate();
          return;
        }

        setMode(data.code);
        setServerMessage(data.message);

        if (data.payload) {
          setCheckPayload(data.payload);
        }
      },
    });
  });

  const changeCreator = (methodName) => {
    return () => {
      mutate(
        createMutateVariables({
          values: getValues(),
          methodName,
          priorityDate: checkPayload,
        }),
        {
          onSuccess: async (data) => {
            if (data === 'Error') return;

            if (data.state === 'Error') {
              setError(fieldName, {
                type: 'server',
                message: data.message || 'Не удалось изменить дату',
              });
              return;
            }

            invalidate();
          },
        }
      );
    };
  };

  const changeAndAdd = useEvent(changeCreator(acceptanceDateMethodNames.add));

  const changeAndDelete = useEvent(
    changeCreator(acceptanceDateMethodNames.delete)
  );

  const afterClose = () => {
    reset();
    resetMode();
    removeProps(modalName);
  };

  const [footerPropsMap] = useState(() =>
    createFooterProps({
      closeModal,
      checkDate,
      resetMode,
      toNewConfig,
      changeAndAdd,
      changeAndDelete,
      handleSubmit,
    })
  );

  const Body = getBody(mutableEntity);

  return (
    <Modal
      onCancel={closeModal}
      visible={isOpen}
      title={getModalTitle(mutableEntity)}
      afterClose={afterClose}
      centered
      destroyOnClose
      footer={<Footer {...footerPropsMap.get(mode)} loading={isLoading} />}
    >
      <form className={styles.form}>
        {Boolean(Body) && (
          <Body
            control={control}
            fromDate={dateTime(acceptance.fromDate)}
            toDate={dateTime(acceptance.toDate)}
            disabled={isLoading || Boolean(serverMessage)}
            changeCb={resetMode}
          />
        )}
      </form>
      {serverMessage && <div style={{ marginTop: 16 }}>{serverMessage}</div>}
    </Modal>
  );
};

export default ChangeAcceptanceDateModal;
